import { GenericLazyString, gettext } from '@eventbrite/i18n';
import React, { useRef, useState } from 'react';
import { ReadMoreCommonProps } from './common';
import { DisplayAmount, DISPLAY_AMOUNT_TEXT_LENGTH_MAP } from './constants';
import { ReadMoreBase } from './ReadMoreBase';
import { ToggleButton } from './ToggleButton';
import { getToggleButtons } from './utils';

export interface TextLengthBasedReadMore extends ReadMoreCommonProps {
    /**
     * When the content is plain text, the display limit is different in mobile
     * and desktop. Use this prop to specify the client device type.
     */
    deviceType: 'mobile' | 'desktop';
    /**
     * Content must be a string or a localized string.
     */
    children: string | GenericLazyString;
}

/**
 * Renders text that is collapsed when has more characters than allowed.
 */
export const TextLengthBasedReadMore: React.FunctionComponent<
    TextLengthBasedReadMore
> = ({
    children,
    collapsedDisplayAmount = DisplayAmount.none,
    collapsedLinkText,
    defaultIsExpanded = false,
    deviceType,
    expandedLinkText,
    onToggleExpanded,
    toggleLocation,
}) => {
    const [isExpanded, setIsExpanded] = useState<boolean>(defaultIsExpanded);
    const contentDivRef = useRef<HTMLDivElement>(null);

    const handleClick = () => {
        const nextExpanded = !isExpanded;
        setIsExpanded(nextExpanded);
        onToggleExpanded?.(nextExpanded);
    };

    const maximumContentLength =
        DISPLAY_AMOUNT_TEXT_LENGTH_MAP[collapsedDisplayAmount][deviceType];
    const shouldTruncateContent = children.length > maximumContentLength;

    const contentToDisplay =
        shouldTruncateContent && !isExpanded
            ? gettext('%(truncatedContent)s…', {
                  truncatedContent: children.slice(0, maximumContentLength),
              })
            : children;

    const toggleButton = (
        <ToggleButton
            collapsedLinkText={collapsedLinkText}
            expandedLinkText={expandedLinkText}
            isExpanded={isExpanded}
            onClick={handleClick}
        />
    );

    const { topButton, bottomButton } = getToggleButtons({
        button: toggleButton,
        shouldTruncateContent,
        toggleLocation,
    });

    return (
        <>
            {topButton}
            <ReadMoreBase
                className="eds-read-more--truncate"
                isExpanded={isExpanded}
                shouldTruncateContent={shouldTruncateContent}
                collapsedDisplayAmount={collapsedDisplayAmount}
                ref={contentDivRef}
            >
                {contentToDisplay}
            </ReadMoreBase>
            {bottomButton}
        </>
    );
};
